import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { ArrowLeft } from "@phosphor-icons/react";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/posts/${slug}`)
      .then(r => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useSeo(post ? {
    title: `${post.title} — Stuffly Blog`,
    description: post.excerpt,
    path: `/blog/${post.slug}`,
    image: post.cover_image,
    type: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "image": post.cover_image ? [post.cover_image] : [],
      "datePublished": post.created_at,
      "dateModified": post.updated_at,
      "author": { "@type": "Organization", "name": post.author || "Stuffly Team" },
      "publisher": {
        "@type": "Organization",
        "name": "Stuffly",
        "logo": { "@type": "ImageObject", "url": typeof window !== "undefined" ? `${window.location.origin}/favicon.ico` : "" }
      },
      "description": post.excerpt,
      "keywords": (post.tags || []).join(", "),
      "mainEntityOfPage": typeof window !== "undefined" ? window.location.href : ""
    }
  } : { title: "Loading… — Stuffly Blog", description: "Loading article", path: "/blog" });

  if (notFound) {
    return (
      <div className="p-16 text-center">
        <h1 className="text-3xl font-black tracking-tighter uppercase font-[Outfit]">Post Not Found</h1>
        <Link to="/blog" className="btn-brutal mt-6 inline-flex">← Back to Blog</Link>
      </div>
    );
  }

  if (!post) return <div className="p-12 text-center text-xs uppercase tracking-widest font-bold font-[Outfit]">Loading…</div>;

  return (
    <article>
      <div className="border-b border-zinc-900 px-6 md:px-12 py-4">
        <Link to="/blog" className="text-xs uppercase tracking-widest font-bold font-[Outfit] hover:underline inline-flex items-center gap-2"><ArrowLeft size={14} weight="bold" /> All Articles</Link>
      </div>

      <header className="border-b border-zinc-900 px-6 md:px-12 py-10 max-w-4xl">
        <div className="flex flex-wrap gap-2 mb-4">
          {(post.tags || []).map(t => <span key={t} className="tag">{t}</span>)}
        </div>
        <h1 className="text-3xl sm:text-5xl font-black tracking-tighter uppercase font-[Outfit] leading-[0.95]" data-testid="post-title">{post.title}</h1>
        <div className="mt-5 text-xs uppercase tracking-widest font-bold font-[Outfit] text-zinc-500">
          {post.author || "Stuffly Team"} · {new Date(post.created_at).toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" })}
        </div>
      </header>

      {post.cover_image && (
        <div className="border-b border-zinc-900 aspect-[21/9] bg-zinc-100 overflow-hidden">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}

      <div
        className="prose prose-lg max-w-3xl px-6 md:px-12 py-10 mx-0
          [&_h2]:font-[Outfit] [&_h2]:font-black [&_h2]:uppercase [&_h2]:tracking-tight [&_h2]:text-2xl [&_h2]:mt-10 [&_h2]:mb-3
          [&_p]:my-4 [&_p]:leading-relaxed [&_p]:text-base
          [&_a]:text-[#ff3333] [&_a]:underline [&_a]:font-semibold
          [&_ul]:my-4 [&_ul]:pl-6 [&_ul]:list-disc [&_li]:my-1
          [&_strong]:font-bold"
        dangerouslySetInnerHTML={{ __html: post.body_html }}
        data-testid="post-body"
      />

      <div className="border-t border-zinc-900 px-6 md:px-12 py-10 bg-zinc-100">
        <div className="text-xs uppercase tracking-widest font-bold font-[Outfit] mb-3">Keep Reading</div>
        <Link to="/blog" className="btn-brutal btn-red">More Articles →</Link>
      </div>
    </article>
  );
}
